import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  GridDisplay,
  Heading2,
  Space,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { CWC_WHATS_NEW } from '../../config'
import { LandingPageContentSections, TrackingActions, trackCTAClick } from '../../tracking'
import whatsnewBoxWebp from '../../assets/WhatsNewSectionAssets/whatsnew-box.webp'
import whatsnewBoxJpg from '../../assets/WhatsNewSectionAssets/whatsnew-box.jpg'
import whatsnewFastWebp from '../../assets/WhatsNewSectionAssets/whatsnew-fast.webp'
import whatsnewFastJpg from '../../assets/WhatsNewSectionAssets/whatsnew-fast.jpg'
import whatsnewHeatEatWebp from '../../assets/WhatsNewSectionAssets/whatsnew-heateat.webp'
import whatsnewHeatEatJpg from '../../assets/WhatsNewSectionAssets/whatsnew-heateat.jpg'

import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { CWCFeatureCard } from './components/CWCFeatureCard'
import { CWCFloatingPills } from '../CWCFloatingPills/CWCFloatingPills'

// Aligned by index to CWC_WHATS_NEW.cards. Each card serves WebP with a JPEG fallback.
const CARD_IMAGES = [
  { webp: whatsnewFastWebp, jpg: whatsnewFastJpg },
  { webp: whatsnewHeatEatWebp, jpg: whatsnewHeatEatJpg },
  { webp: whatsnewBoxWebp, jpg: whatsnewBoxJpg },
]

// Dark section background: Citrus `ColdGrey_800` (#333D47). Desktop vertical padding matches the
// Figma "Section" frame (node 1-480): 80px top / 99px bottom (asymmetric, so set here rather than
// via paddingV); mobile keeps 48px top/bottom.
const SectionContainer = withCitrusTheme(Box, (theme) => [
  { backgroundColor: theme.color(Color.ColdGrey_800) },
  theme.responsiveStyle(['48px', null, '80px'], ['paddingTop']),
  theme.responsiveStyle(['48px', null, '99px'], ['paddingBottom']),
])

// "What's new" heading: Axiforma ExtraBold 36px / 32px line-height (exact Figma).
// Uses responsiveStyle (media-query specificity) so it overrides Heading2's own
// responsive font-size at every breakpoint.
const SectionHeading = withCitrusTheme(Heading2, (theme) => [
  theme.responsiveStyle(['36px', null, '36px'], ['fontSize']),
  theme.responsiveStyle(['32px', null, '32px'], ['lineHeight']),
])

// Equal-height cards: a single-column grid with `grid-auto-rows: 1fr` sizes every row to the tallest
// card's content, so the cards stay uniform while growing to fit their copy (no overflow at narrow
// widths like iPhone SE). `gridAutoRows` isn't a Box prop, so it's set here.
const CardsGrid = withCitrusTheme(Box, () => ({
  gridAutoRows: '1fr',
}))

export type CWCWhatsNewSectionProps = {
  signupCTA: string
  /** Sign-up destination the CTA links to. */
  signupUrl: string
}

export const CWCWhatsNewSection = ({ signupCTA, signupUrl }: CWCWhatsNewSectionProps) => {
  const trackSignupClick = () => {
    trackCTAClick(TrackingActions.CLICK_GET_STARTED, LandingPageContentSections.WHATS_NEW)
  }

  return (
    <section data-testid="whatsNewSection">
      <SectionContainer
        paddingH={[4, null, 6]}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.Center}
      >
        <SectionHeading
          color={Color.White}
          fontFamily={FontFamily.ExtraBold}
          textAlign={TextAlign.Center}
        >
          {CWC_WHATS_NEW.heading}
        </SectionHeading>

        <Space size={8} />

        <CardsGrid
          maxWidth="640px"
          width="100%"
          display={GridDisplay.Grid}
          gridTemplateColumns="1fr"
          gap={6}
        >
          {CWC_WHATS_NEW.cards.map((card, index) => (
            <CWCFeatureCard
              key={card.title}
              title={card.title}
              body={card.body}
              image={CARD_IMAGES[index]}
              // The "Built for real life" (box) card carries the decorative floating pills.
              imageOverlay={card.title === 'Built for real life' ? <CWCFloatingPills /> : undefined}
            />
          ))}
        </CardsGrid>

        <Space size={10} />

        <CWCLinkButton href={signupUrl} onClick={trackSignupClick}>
          {signupCTA}
        </CWCLinkButton>
      </SectionContainer>
    </section>
  )
}
